import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { Partner } from 'types';

export type ProviderReference = IOnboardingSettings['provider'];

export const filterStoragePartners = (partners: Partner[]): Partner[] =>
	partners.filter(
		(p) =>
			p.label?.toLowerCase().includes('google') ||
			p.label?.toLowerCase().includes('drive') ||
			p.label?.toLowerCase().includes('onedrive') ||
			p.label?.toLowerCase().includes('microsoft')
	);

import { resolvePartnerId } from 'core/API/partners';

export const getPartnerIri = (partner: Partner): string =>
	partner['@id'] || `/api/partner_lists/${resolvePartnerId(partner)}`;

export const normalizePartnerIri = (iri: string): string => {
	if (!iri) return '';
	const apiIndex = iri.indexOf('/api/');
	if (apiIndex >= 0) {
		return iri.slice(apiIndex);
	}
	return iri.startsWith('/') ? iri : `/${iri}`;
};

const isPartnerIri = (value: string): boolean =>
	value.includes('/api/') || value.includes('partner_lists');

const extractFromReference = (
	reference: ProviderReference
): { iri: string | null; label: string | null } => {
	if (!reference) {
		return { iri: null, label: null };
	}

	if (typeof reference === 'string') {
		const trimmed = reference.trim();
		if (!trimmed) {
			return { iri: null, label: null };
		}
		if (isPartnerIri(trimmed)) {
			return { iri: trimmed, label: null };
		}
		return { iri: null, label: trimmed };
	}

	if (typeof reference === 'object') {
		const iri = typeof reference['@id'] === 'string' ? reference['@id'] : null;
		const label = typeof reference.label === 'string' ? reference.label : null;
		return { iri, label };
	}

	return { iri: null, label: null };
};

export const extractStorageProviderFields = (
	settings: IOnboardingSettings | null | undefined
): { iri: string | null; label: string | null } => {
	if (!settings) {
		return { iri: null, label: null };
	}

	const rawSettings = settings as IOnboardingSettings & {
		storage_provider?: ProviderReference;
	};

	const storageRef = rawSettings.storageProvider ?? rawSettings.storage_provider;
	const providerRef = rawSettings.provider;

	const fromStorage = extractFromReference(storageRef as ProviderReference);
	const fromProvider = extractFromReference(providerRef as ProviderReference);

	return {
		iri: fromProvider.iri ?? fromStorage.iri,
		label: fromProvider.label ?? fromStorage.label,
	};
};

export const isStorageProviderUnset = (
	settings: IOnboardingSettings | null | undefined
): boolean => {
	if (!settings) return false;
	const { iri, label } = extractStorageProviderFields(settings);
	return !iri && !label;
};

const matchesStorageProviderSlug = (storageProvider: string, partner: Partner): boolean => {
	const slug = storageProvider.toLowerCase();
	const label = partner.label?.toLowerCase() || '';

	if (label === slug || label.includes(slug) || slug.includes(label)) {
		return true;
	}

	if (slug.includes('google') || slug.includes('drive')) {
		return label.includes('google') || label.includes('drive');
	}

	if (slug.includes('onedrive') || slug.includes('microsoft')) {
		return label.includes('onedrive') || label.includes('microsoft');
	}

	return false;
};

const findPartnerByIriValue = (partners: Partner[], iri: string): Partner | null => {
	const normalized = normalizePartnerIri(iri);
	return partners.find((p) => normalizePartnerIri(getPartnerIri(p)) === normalized) ?? null;
};

const findPartnerByLabelValue = (partners: Partner[], label: string): Partner | null => {
	const normalizedLabel = label.toLowerCase();
	return (
		partners.find((p) => p.label?.toLowerCase() === normalizedLabel) ??
		partners.find((p) => matchesStorageProviderSlug(label, p)) ??
		null
	);
};

export const resolveStoragePartner = (
	settings: IOnboardingSettings | null | undefined,
	partners: Partner[]
): Partner | null => {
	if (!settings || partners.length === 0) return null;
	if (isStorageProviderUnset(settings)) return null;

	const { iri, label } = extractStorageProviderFields(settings);

	if (iri) {
		const byIri = findPartnerByIriValue(partners, iri);
		if (byIri) return byIri;
	}

	if (label) {
		const byLabel = findPartnerByLabelValue(partners, label);
		if (byLabel) return byLabel;
	}

	return null;
};

export const getStorageProviderDisplayLabel = (
	settings: IOnboardingSettings | null | undefined,
	partners: Partner[]
): string => {
	if (!settings || isStorageProviderUnset(settings)) {
		return 'Not configured';
	}

	const storagePartners = filterStoragePartners(partners);
	const resolved =
		resolveStoragePartner(settings, storagePartners) ?? resolveStoragePartner(settings, partners);

	if (resolved?.label) {
		return resolved.label;
	}

	const { label } = extractStorageProviderFields(settings);
	return label || 'Not configured';
};

export const findPartnerByIri = (partners: Partner[], partnerIri: string): Partner | undefined => {
	return findPartnerByIriValue(partners, partnerIri) ?? undefined;
};
